import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import "../../styles/AdminStyling/SignUpForm.css";

// A compact slider toggle component.
// When checked, it shows the "ON" label; when not, it shows "OFF".
// You can customize the labels via the labelOn and labelOff props.
const ToggleSwitch = ({ checked, onChange }) => (
  <label className="toggle-switch">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="toggle-slider-add" />
  </label>
);


export const SignUpForm = ({ closeForm, onSuccess }) => {
  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    password: "",
    role: [],
    company: [],
    isActivated: "Yes",
    autoGeneratePassword: true,
  });
  const [sponsors, setSponsors] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Roles constant for react-select options
  const roles = [
    { value: "Admin", label: "Admin" },
    { value: "Leadership", label: "Leadership" },
    { value: "Sponsor", label: "Sponsor" },
    { value: "Fellow", label: "Fellow" },
    { value: "Co-ooooop/Intern", label: "Co-op/Intern" },
    { value: "Board Member", label: "Board Member" },
  ];

  // Fetch sponsors from API
  const fetchSponsors = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sponsors`
      );
      const data = res.data;
      const sponsorList = Array.isArray(data) ? data : data.sponsors || [];
      setSponsors(sponsorList);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  // Simplified password generator for demonstration
  const generatePassword = () => {
    return "Auto Generated Password!";
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle multi-select changes for roles and sponsors
  const handleRoleChange = (selectedOptions) => {
    setFormData({ ...formData, role: selectedOptions || [] });
  };

  const handleSponsorChange = (selectedOptions) => {
    setFormData({ ...formData, company: selectedOptions || [] });
  };

  // Toggle the auto-generate password mode
  const toggleAutoGeneratePassword = () => {
    const isGenerating = !formData.autoGeneratePassword;
    setFormData({
      ...formData,
      autoGeneratePassword: isGenerating,
      password: isGenerating ? generatePassword() : "",
    });
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    // Prepare payload by converting multi-selects to arrays of strings
    const payload = {
      ...formData,
      role: formData.role.map((option) => option.value),
      company: formData.company.map((option) => option.value),
    };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/signup`,
        payload
      );
      if (response.data.success) {
        setSuccessMessage("User signed up successfully!");
        setError("");
        setTimeout(() => {
          onSuccess && onSuccess();
          closeForm();
        }, 1500);
      } else {
        setError(response.data.message || "Sign-up failed. Please try again.");
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response.data.message || "An error occurred. Please try again."
        );
      } else {
        setError("Network error. Please check your connection.");
      }
    }
  };

  // Build sponsor options (using spname as value/label)
  const sponsorOptions = sponsors.map((sponsor) => ({
    value: sponsor.spname,
    label: sponsor.spname,
  }));

  return (
    <div className="signup-popup signup-popup--overlay">
      <form className="signup-form" onSubmit={handleSignUp}>
        <h2 className="signup-form__title">Add User</h2>
        {error && <p className="signup-form__error error">{error}</p>}
        {successMessage && (
          <p className="signup-form__success success">{successMessage}</p>
        )}

        <div className="form-group form-group--fname">
          <label className="form-group__label" htmlFor="fname">
            First Name
          </label>
          <input
            id="fname"
            type="text"
            className="form-group__input"
            name="fname"
            value={formData.fname}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group form-group--lname">
          <label className="form-group__label" htmlFor="lname">
            Last Name
          </label>
          <input
            id="lname"
            type="text"
            className="form-group__input"
            name="lname"
            value={formData.lname}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group form-group--email">
          <label className="form-group__label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="form-group__input"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group form-group--password password-group" style={{flexDirection: "column"}}>
          <label className="form-group__label" htmlFor="password">
            Password
          </label>
          <div style={{display: "flex", flexDirection: "row", width: "100%"}}>
            {formData.autoGeneratePassword ? (
              <p className="generated-password">
                {formData.password.length > 0
                  ? formData.password
                  : "Auto Generated Password!"}
              </p>
            ) : (
              <input
                id="password"
                type="password"
                className="form-group__input"
                name="password"
                value={formData.password}
                placeholder="Enter Password Manually!"
                onChange={handleChange}
                required
              />
            )}
            <div className="toggle-switch-wrapper" style={{marginLeft: "10px"}}>
              <div className="toggle-switch-wrapper">
                <ToggleSwitch
                  checked={!formData.autoGeneratePassword}
                  onChange={toggleAutoGeneratePassword}
                />
                <small className="toggle-caption">
                  Set Password Manually
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="form-group form-group--select">
          <label className="form-group__label" htmlFor="role">
            Role (Select multiple)
          </label>
          <Select
            id="role"
            name="role"
            className="form-group__select"
            isMulti
            options={roles}
            value={formData.role}
            onChange={handleRoleChange}
            placeholder="Select roles..."
          />
        </div>

        <div className="form-group form-group--select">
          <label className="form-group__label" htmlFor="company">
            Sponsor (Select multiple)
          </label>
          <Select
            id="company"
            name="company"
            className="form-group__select"
            isMulti
            options={sponsorOptions}
            value={formData.company}
            onChange={handleSponsorChange}
            placeholder="Select sponsors..."
          />
        </div>

        <div className="form-group item-actions">
          <button type="submit" className="submit-btn">
            Add User
          </button>
          <button type="button" className="cancel-btn" onClick={closeForm}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default SignUpForm;
